import { IndividualRelationshipType, OrganisationRelationshipType, Priority } from '@/shared/types/enum';
import { ModelName } from '@/shared/types/model';
import { enumOptions } from '@/shared/utils/enum';

export type BulkUpdateControlType = 'lookup' | 'enum' | 'date' | 'archiveAction' | 'text';

export type BulkUpdateFieldConfig = {
  field: string;
  label: string;
  control: BulkUpdateControlType;
  clearable: boolean;
  lookupModel?: ModelName;
  options?: { value: string | number; text: string }[];
  fixedValue?: string | number;
};

export enum BulkArchiveAction {
  Archive = 'Archive',
  Unarchive = 'Unarchive',
}

export const bulkUpdateFields: Partial<Record<ModelName, BulkUpdateFieldConfig[]>> = {
  Organisation: [
    {
      field: 'relationshipType',
      label: 'Relationship',
      control: 'enum',
      clearable: false,
      options: enumOptions(OrganisationRelationshipType),
    },
    {
      field: 'partnerId',
      label: 'Partner',
      control: 'lookup',
      clearable: true,
      lookupModel: 'MemberView',
    },
    {
      field: 'accountManagerId',
      label: 'Account Manager',
      control: 'lookup',
      clearable: false,
      lookupModel: 'MemberView',
    },
    {
      field: 'groupCompanyId',
      label: 'Group',
      control: 'lookup',
      clearable: true,
      lookupModel: 'Organisation',
    },
    {
      field: 'notes',
      label: 'Note',
      control: 'text',
      clearable: true,
    },
    {
      field: 'archivedTime',
      label: 'Archive',
      control: 'archiveAction',
      clearable: false,
    },
  ],
  Individual: [
    {
      field: 'relationshipType',
      label: 'Relationship',
      control: 'enum',
      clearable: false,
      options: enumOptions(IndividualRelationshipType),
    },
    {
      field: 'partnerId',
      label: 'Partner',
      control: 'lookup',
      clearable: true,
      lookupModel: 'MemberView',
    },
    {
      field: 'accountManagerId',
      label: 'Account Manager',
      control: 'lookup',
      clearable: false,
      lookupModel: 'MemberView',
    },
    {
      field: 'notes',
      label: 'Note',
      control: 'text',
      clearable: true,
    },
    {
      field: 'archivedTime',
      label: 'Archive',
      control: 'archiveAction',
      clearable: false,
    },
  ],
  Task: [
    {
      field: 'recordsReceivedDate',
      label: 'Records Received',
      control: 'date',
      clearable: true,
    },
    {
      field: 'startedDate',
      label: 'Started On',
      control: 'date',
      clearable: true,
    },
    {
      field: 'completedDate',
      label: 'Completed On',
      control: 'date',
      clearable: true,
    },
    {
      field: 'assignedToUserId',
      label: 'Assigned To',
      control: 'lookup',
      clearable: true,
      lookupModel: 'MemberView',
    },
    {
      field: 'priority',
      label: 'Priority',
      control: 'enum',
      clearable: false,
      options: enumOptions(Priority),
    },
    {
      field: 'notes',
      label: 'Note',
      control: 'text',
      clearable: true,
    },
  ],
};

export const getBulkUpdateFields = (
  modelName: ModelName,
  options?: { archived?: boolean },
): BulkUpdateFieldConfig[] => {
  const fields = bulkUpdateFields[modelName] ?? [];
  const archived = options?.archived ?? false;

  return fields.map((field) => {
    if (field.control !== 'archiveAction') {
      return field;
    }

    return {
      ...field,
      label: archived ? 'Unarchive' : 'Archive',
      fixedValue: archived ? BulkArchiveAction.Unarchive : BulkArchiveAction.Archive,
    };
  });
};

export const getBulkUpdateField = (modelName: ModelName, field: string): BulkUpdateFieldConfig | undefined =>
  (bulkUpdateFields[modelName] ?? []).find((f) => f.field === field);

export const BULK_UPDATE_MODELS = ['Organisation', 'Individual', 'Task'] as const;

export type BulkUpdateModelName = (typeof BULK_UPDATE_MODELS)[number];

export const isBulkUpdateModel = (modelName: string): modelName is BulkUpdateModelName =>
  (BULK_UPDATE_MODELS as readonly string[]).includes(modelName);
